import { useState } from 'react';
import { ArrowLeftOutlined, CopyOutlined } from '@ant-design/icons';
import { Alert, App, Button, Card, Divider, Form, Input, QRCode, Space, Steps, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/http/apiClient';
import { authApi } from '../api/authApi';
import { useAuth } from '../context/AuthContext';
import { readLoginEmail } from '../storage';

interface SetupFormValues {
  email: string;
  enrollmentToken: string;
}

interface CompleteFormValues {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Primer acceso: paso 1 valida la invitación y muestra el QR TOTP; paso 2 define la
 * contraseña + primer código y abre sesión (`/api/v1/admin/auth/enroll/setup` + `/api/v1/admin/auth/enroll/complete`).
 */
export function EnrollPage() {
  const { completeEnrollment } = useAuth();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [current, setCurrent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupData, setSetupData] = useState<SetupFormValues | null>(null);
  const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null);

  const handleSetup = async (values: SetupFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await authApi.enrollSetup(values.email, values.enrollmentToken);
      setSetupData(values);
      setOtpauthUrl(response.otpauthUrl);
      setCurrent(1);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se pudo validar la invitación.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async (values: CompleteFormValues) => {
    if (!setupData) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await completeEnrollment({
        email: setupData.email,
        enrollmentToken: setupData.enrollmentToken,
        newPassword: values.newPassword,
        code: values.code,
      });
      message.success('Configuración completada. Bienvenido.');
      navigate('/noc', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se pudo completar la configuración.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell">
      <Card className="auth-card auth-card--wide">
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          Configurar doble factor
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Primer acceso con invitación. Necesitas una app autenticador (Google Authenticator, Authy, 1Password).
        </Typography.Paragraph>

        <Steps
          current={current}
          size="small"
          items={[{ title: 'Invitación' }, { title: 'Autenticador' }]}
          style={{ marginBottom: 24 }}
        />

        {error && (
          <Alert
            type="error"
            showIcon
            closable
            message={error}
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        {current === 0 && (
          <Form<SetupFormValues>
            layout="vertical"
            onFinish={handleSetup}
            requiredMark={false}
            initialValues={{ email: readLoginEmail() ?? '' }}
          >
            <Form.Item
              label="Correo"
              name="email"
              rules={[
                { required: true, message: 'Ingresa tu correo' },
                { type: 'email', message: 'Correo inválido' },
              ]}
            >
              <Input autoComplete="username" size="large" autoFocus />
            </Form.Item>
            <Form.Item
              label="Token de invitación"
              name="enrollmentToken"
              rules={[{ required: true, message: 'Ingresa el token de tu invitación' }]}
            >
              <Input size="large" placeholder="Token del correo de invitación" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 8 }}>
              <Button type="primary" htmlType="submit" block size="large" loading={submitting}>
                Generar código QR
              </Button>
            </Form.Item>
          </Form>
        )}

        {current === 1 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Space direction="vertical" align="center" size="small" style={{ width: '100%' }}>
              <Typography.Text type="secondary">
                Escanea el QR con tu app autenticador y luego ingresa el código de 6 dígitos.
              </Typography.Text>
              {otpauthUrl && (
                <QRCode value={otpauthUrl} size={180} aria-label="Código QR para configurar 2FA" />
              )}
            </Space>

            <Divider style={{ margin: 0 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <CopyOutlined /> También puedes copiar el enlace `otpauth`
              </Typography.Text>
            </Divider>

            <Form<CompleteFormValues> layout="vertical" onFinish={handleComplete} requiredMark={false}>
              <Form.Item
                label="Código de verificación"
                name="code"
                rules={[
                  { required: true, message: 'Ingresa el código' },
                  { len: 6, message: 'El código tiene 6 dígitos' },
                ]}
              >
                <Input.OTP length={6} size="large" inputMode="numeric" />
              </Form.Item>
              <Form.Item
                label="Nueva contraseña"
                name="newPassword"
                rules={[
                  { required: true, message: 'Define tu contraseña' },
                  { min: 8, message: 'Mínimo 8 caracteres' },
                ]}
                hasFeedback
              >
                <Input.Password autoComplete="new-password" size="large" />
              </Form.Item>
              <Form.Item
                label="Confirmar contraseña"
                name="confirmPassword"
                dependencies={['newPassword']}
                hasFeedback
                rules={[
                  { required: true, message: 'Confirma tu contraseña' },
                  ({ getFieldValue }) => ({
                    validator(_rule, value: string) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Las contraseñas no coinciden'));
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" size="large" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 8 }}>
                <Button type="primary" htmlType="submit" block size="large" loading={submitting}>
                  Activar y entrar
                </Button>
              </Form.Item>
            </Form>
          </Space>
        )}

        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/login', { replace: true })}>
          Volver al login
        </Button>
      </Card>
    </div>
  );
}
